import useUserNotes from '@hooks/query/user/useUserNotes';
import { useVirtualizer } from '@tanstack/react-virtual';
import { HearthBeatLoader, Loader } from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import AddNoteForm from './components/add-note-form';
import NoteItem from './components/note-item';
import { NotesListSC, NotesWrapSC } from './notes.styled';

type TProps = {
  userId: string;
};

const Notes: React.FC<TProps> = ({ userId }) => {
  const { formatMessage } = useIntl();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useUserNotes(userId);

  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? data.length + 1 : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
    paddingStart: 16,
  });

  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= data.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, data.length, isFetchingNextPage, items]);

  return (
    <NotesWrapSC>
      <NotesListSC
        ref={parentRef}
        className="List"
        style={{
          height: '100%',
          width: '100%',
          overflowY: 'auto',
          contain: 'strict',
        }}
      >
        {isLoading ? (
          <div className="loader">
            <HearthBeatLoader
              loading
              size={'medium'}
              style={{
                marginLeft: 'auto',
                marginRight: 'auto',
                marginTop: '50px',
                marginBottom: '50px',
              }}
              color="#777E9B"
            />
          </div>
        ) : (
          <div
            style={{
              height: rowVirtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${items[0]?.start}px)`,
              }}
            >
              {items?.map((virtualRow) => {
                const isLoaderRow = virtualRow.index > data.length - 1;
                const note = data[virtualRow.index];

                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                  >
                    {isLoaderRow ? (
                      hasNextPage ? (
                        <div className="load-more">
                          <Loader size="small" />
                        </div>
                      ) : (
                        <div className="load-more">
                          {formatMessage({ id: 'life_id.notes.load.more' })}
                        </div>
                      )
                    ) : (
                      <NoteItem key={note.id} note={note.note} createdAt={note.createdAt} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </NotesListSC>
      <AddNoteForm afterSubmit={refetch} userId={userId} />
    </NotesWrapSC>
  );
};

export default React.memo(Notes);
